import React, { useState } from 'react';
import {
	Alert,
	Checkbox,
	Flex,
	Form,
	Input,
	Skeleton,
	Space,
} from 'antd';
import { useQuery } from 'react-query';
import {
	ExpenseTypes,
	FixtureCounterpartyTypes,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import type { GetVoyagesResponse } from '@api/features/voyages/getVoyages';
import { getCounterparties } from '@client/lib/api';
import { rules } from '@client/utils/form';
import { useAuth } from '@client/lib/auth';
import TooltipIcon from '@client/components/TooltipIcon';
import Select from '../Select';
import MultiCurrencyInput from '../MultiCurrencyInput';
import Button from '../Button';

const EuExpenseVcFormItems = ({
	expenseType,
	voyages,
	voyageId,
	isEditing,
	extra,
}: {
	voyages: GetVoyagesResponse['rows'] | undefined;
	expenseType: Values<typeof ExpenseTypes>;
	voyageId: number;
	isEditing: boolean;
	extra?: React.ReactElement;
}) => {
	const { userInfo } = useAuth();

	const [selectedType, setSelectedType] = useState<Values<typeof ExpenseTypes> | null>(null);
	const [isAmountNegative, setIsAmountNegative] = useState(false);

	const selectedVoyage = (voyages ?? []).find((v) => v.id === voyageId);

	const {
		data: charterers,
	} = useQuery(
		'charterers',
		() => getCounterparties(FixtureCounterpartyTypes.CHARTEREROWNER),
	);

	return (
		<>
			<Form.Item
				label="Type"
				name="type"
			>
				<Select<Values<typeof ExpenseTypes>>
					placeholder="Select type"
					disabled={isEditing}
					showSearch
					allowClear
					onChange={(v) => setSelectedType(v)}
					options={[
						{ label: 'Payable', value: ExpenseTypes.PAYABLE },
						{ label: 'Payable & Receivable', value: ExpenseTypes.BOTH },
					]}
				/>
			</Form.Item>
			{expenseType == null ? (
				<>
					<p>Please select a type</p>
					<Skeleton />
				</>
			) : (
				<>
					{expenseType === ExpenseTypes.BOTH && (
						<Form.Item
							name="charterer"
							label="Charterer"
							rules={[rules.required]}
						>
							<Select
								options={charterers?.map((c) => ({
									label: c.name, value: c.id,
								}))}
								disabled={isEditing && expenseType === ExpenseTypes.BOTH}
							/>
						</Form.Item>
					)}
					<Form.Item
						name="estimated"
						valuePropName="checked"
						initialValue={false}
						label={(
							<Space>
								Estimated
								<TooltipIcon>
									Estimated items are used for estimating P&L items,
									and cannot be invoiced or put through
									an approvals process.
									If you wish to invoice the item,
									convert the item to an actual by
									deselecting the &quot;estimated&quot; checkbox
								</TooltipIcon>
							</Space>
						)}
					>
						<Checkbox />
					</Form.Item>
					<Form.Item>
						<Flex vertical gap={0}>
							<Form.Item
								name="amount"
								label={(() => 'Invoice Amount')()}
								rules={[rules.required]}
							>
								<MultiCurrencyInput
									inputValues={undefined}
									allowNegative={selectedType === ExpenseTypes.PAYABLE}
									baseCurrency={selectedVoyage?.currency || userInfo.baseCurrency}
									placeholder="Amount"
									onChange={(v) => setIsAmountNegative(v.value != null ? v.value < 0 : false)}
								/>
							</Form.Item>
						</Flex>
						{isAmountNegative && (
							<Alert
								message={(
									<>
										<b>NOTE:</b>
										{' '}
										The amount being entered should only be negative for e.g. rebates.
									</>
								)}
							/>
						)}
					</Form.Item>
					<Form.Item
						label="Amount type"
						name="isRevenue"
					>
						<Select
							placeholder="Select type"
							showSearch
							allowClear
							defaultValue={false}
							options={[
								{ label: 'Revenue', value: true },
								{ label: 'Expense', value: false },
							]}
						/>
					</Form.Item>
					<Form.Item
						name="note"
						label="Internal note"
					>
						<Input.TextArea />
					</Form.Item>
					{extra}
					<Button
						htmlType="submit"
						type="primary"
					>
						Save Expense
					</Button>
				</>
			)}
		</>
	);
};

export default EuExpenseVcFormItems;
